import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { getApiBase } from "../apiBase";
import { 
  FaUsers, 
  FaEnvelope, 
  FaPhone, 
  FaIdCard, 
  FaChevronDown, 
  FaChevronUp, 
  FaRobot, 
  FaCommentAlt,
  FaClock,
  FaSearch,
  FaPen,
  FaCodeBranch,
  FaCheck,
  FaTimes,
  FaExclamationTriangle
} from "react-icons/fa";
import { motion, AnimatePresence } from "framer-motion";

const E164_RE = /^\+[0-9]{7,15}$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const UserRecords = () => {
  const { tenantId, botId } = useParams();

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedUser, setExpandedUser] = useState(null);
  const [expandedSession, setExpandedSession] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  // Edit state
  const [editingUser, setEditingUser] = useState(null);
  const [editDraft, setEditDraft] = useState({});
  const [editErrors, setEditErrors] = useState({});
  const [saving, setSaving] = useState(false);

  // Merge state
  const [mergingUser, setMergingUser] = useState(null);
  const [duplicates, setDuplicates] = useState([]);
  const [mergeTarget, setMergeTarget] = useState(null);
  const [mergeStep, setMergeStep] = useState(1);
  const [mergeLoading, setMergeLoading] = useState(false);
  const [mergeSearch, setMergeSearch] = useState("");
  const [mergeSearchResults, setMergeSearchResults] = useState([]);
  const [mergeSearching, setMergeSearching] = useState(false);

  const colors = {
    primary: "#5B3A60",
    secondary: "#7B5E57",
    accent: "#B89D4F",
    bgSoft: "#FAF7F5",
    border: "#D6C3A1",
    textMuted: "#6B7280",
    textLight: "#A89CA5"
  };

  const fetchUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${getApiBase()}/user/records/${tenantId}/${botId}`);
      if (!res.ok) throw new Error("Failed to fetch");
      const json = await res.json();
      const validUsers = (json.data || []).filter(u => u.name && u.email && u.phone);
      setUsers(validUsers);
    } catch (err) {
      console.error(err);
      setError("Failed to load user records.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (tenantId && botId) fetchUsers();
  }, [tenantId, botId]);

  const formatDate = (date) => {
    if (!date) return "—";
    const parsed = new Date(date.replace(" ", "T") + "Z");
    return parsed.toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
  };

  const filteredUsers = users.filter(u =>
    u.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.end_user_id.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // ── Edit helpers ─────────────────────────────────────────────────────────────

  const openEdit = (e, u) => {
    e.stopPropagation();
    setExpandedUser(null);
    setEditingUser(u.end_user_id);
    setEditDraft({ name: u.name || "", email: u.email || "", phone: u.phone || "" });
    setEditErrors({});
  };

  const cancelEdit = (e) => {
    e && e.stopPropagation();
    setEditingUser(null);
    setEditDraft({});
    setEditErrors({});
  };

  const validateDraft = (draft) => {
    const errs = {};
    if (!draft.name || !draft.name.trim()) errs.name = "Name is required";
    if (!draft.email || !EMAIL_RE.test(draft.email)) errs.email = "Valid email required";
    if (!draft.phone || !E164_RE.test(draft.phone)) errs.phone = "E.164 phone required (+digits, 7–15 digits)";
    return errs;
  };

  const saveEdit = async (e, u) => {
    e.stopPropagation();
    const errs = validateDraft(editDraft);
    if (Object.keys(errs).length) { setEditErrors(errs); return; }
    setSaving(true);
    try {
      const body = {};
      if (editDraft.name  !== u.name)  body.name  = editDraft.name;
      if (editDraft.email !== u.email) body.email = editDraft.email;
      if (editDraft.phone !== u.phone) body.phone = editDraft.phone;

      const res = await fetch(
        `${getApiBase()}/user/records/${tenantId}/${botId}/${u.end_user_id}`,
        { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) }
      );
      if (!res.ok) {
        const detail = await res.json().catch(() => ({}));
        throw new Error(detail.detail || "Save failed");
      }
      // Update local state immediately
      setUsers(prev => prev.map(x =>
        x.end_user_id === u.end_user_id
          ? { ...x, name: editDraft.name, email: editDraft.email, phone: editDraft.phone }
          : x
      ));
      setEditingUser(null);
      // Refetch to ensure consistent state
      fetchUsers();
    } catch (err) {
      setEditErrors({ _save: err.message });
    } finally {
      setSaving(false);
    }
  };

  // ── Merge helpers ─────────────────────────────────────────────────────────────

  const openMerge = async (e, u) => {
    e.stopPropagation();
    setMergingUser(u);
    setMergeTarget(null);
    setMergeStep(1);
    setMergeSearch("");
    setMergeSearchResults([]);
    const params = new URLSearchParams({ phone: u.phone || "", email: u.email || "" });
    try {
      const res = await fetch(
        `${getApiBase()}/user/records/duplicates/${tenantId}/${botId}/${u.end_user_id}?${params}`
      );
      const json = await res.json();
      setDuplicates(json.duplicates || []);
    } catch {
      setDuplicates([]);
    }
  };

  const closeMerge = () => {
    setMergingUser(null);
    setDuplicates([]);
    setMergeTarget(null);
    setMergeStep(1);
    setMergeSearch("");
    setMergeSearchResults([]);
  };

  const handleMergeSearch = async (val) => {
    setMergeSearch(val);
    if (!val.trim()) { setMergeSearchResults([]); return; }
    setMergeSearching(true);
    try {
      const params = new URLSearchParams({ phone: val, email: val });
      const res = await fetch(
        `${getApiBase()}/user/records/duplicates/${tenantId}/${botId}/${mergingUser.end_user_id}?${params}`
      );
      const json = await res.json();
      setMergeSearchResults(json.duplicates || []);
    } catch {
      setMergeSearchResults([]);
    } finally {
      setMergeSearching(false);
    }
  };

  const confirmMerge = async () => {
    if (!mergeTarget || !mergingUser) return;
    setMergeLoading(true);
    try {
      const res = await fetch(`${getApiBase()}/user/records/merge`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          tenant_id: tenantId,
          bot_id: botId,
          source_end_user_id: mergingUser.end_user_id,
          target_end_user_id: mergeTarget.end_user_id,
        }),
      });
      if (!res.ok) {
        const detail = await res.json().catch(() => ({}));
        throw new Error(detail.detail || "Merge failed");
      }
      // Remove source card, refresh list for updated target session count
      setUsers(prev => prev.filter(x => x.end_user_id !== mergingUser.end_user_id));
      closeMerge();
      fetchUsers();
    } catch (err) {
      alert(`Merge failed: ${err.message}`);
    } finally {
      setMergeLoading(false);
    }
  };

  // Candidates shown in modal Step 1: auto-suggestions + search results (deduped)
  const mergeStepOneCandidates = [
    ...duplicates,
    ...mergeSearchResults.filter(r => !duplicates.find(d => d.end_user_id === r.end_user_id)),
  ];

  // COALESCE preview for Step 2
  const mergePreview = mergingUser && mergeTarget ? {
    name:  mergeTarget.name  || mergingUser.name,
    email: mergeTarget.email || mergingUser.email,
    phone: mergeTarget.phone || mergingUser.phone,
  } : null;

  // ── Render ────────────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-[#FAF7F5] p-6 font-sans text-[#5B3A60]">
      {/* Header */}
      <div className="max-w-7xl mx-auto mb-8">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold tracking-tight flex items-center gap-3">
              <div className="p-2 bg-[#B89D4F]/20 rounded-xl text-[#B89D4F]">
                <FaUsers size={24} />
              </div>
              Potential Leads
            </h1>
            <p className="mt-1 text-sm text-[#6B7280]">
              View, edit, and merge end-user identity records for this assistant.
            </p>
          </div>
          <div className="relative w-full md:w-72">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <FaSearch className="text-[#A89CA5]" />
            </div>
            <input
              type="text"
              placeholder="Search by name, email or ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-[#D6C3A1]/50 bg-white focus:outline-none focus:ring-2 focus:ring-[#B89D4F] focus:border-transparent shadow-sm text-sm transition-all"
            />
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto">
        {loading && (
          <div className="flex flex-col items-center justify-center py-20">
            <div className="w-10 h-10 rounded-full border-[3px] border-[#B89D4F] border-t-transparent animate-spin"></div>
            <p className="mt-4 text-sm font-medium text-[#5B3A60]">Loading records...</p>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-xl flex items-center gap-3">
            <span className="font-bold">Error:</span> {error}
          </div>
        )}

        {!loading && !error && filteredUsers.length === 0 && (
          <div className="text-center py-20 bg-white rounded-2xl border border-dashed border-[#D6C3A1]">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-purple-50 text-[#B89D4F] mb-4">
              <FaUsers size={24} />
            </div>
            <h3 className="text-lg font-bold text-[#5B3A60]">No users found</h3>
            <p className="text-sm text-[#6B7280] mt-1">
              {searchTerm ? "Try adjusting your search terms." : "No user records available for this bot yet."}
            </p>
          </div>
        )}

        {!loading && filteredUsers.length > 0 && (
          <div className="space-y-4">
            {filteredUsers.map((u) => {
              const isExpanded = expandedUser === u.end_user_id;
              const isEditing  = editingUser  === u.end_user_id;

              return (
                <motion.div
                  key={u.end_user_id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`bg-white rounded-2xl border transition-all duration-300 overflow-hidden shadow-sm ${
                    isExpanded
                      ? "border-[#B89D4F] shadow-[0_8px_30px_rgba(91,58,96,0.1)]"
                      : "border-[#D6C3A1]/40 hover:border-[#B89D4F]/50 hover:shadow-md"
                  }`}
                >
                  {/* ── User Summary Row ── */}
                  {!isEditing && (
                    <div
                      className="p-5 cursor-pointer flex items-center justify-between"
                      onClick={() => setExpandedUser(isExpanded ? null : u.end_user_id)}
                    >
                      <div className="flex items-center gap-4">
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-lg font-bold text-white shadow-md ${
                          isExpanded ? "bg-[#5B3A60]" : "bg-gradient-to-br from-[#823B55] to-[#5B3A60]"
                        }`}>
                          {u.name.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <h3 className="font-bold text-[#5B3A60] text-lg">{u.name}</h3>
                          <div className="flex items-center gap-3 text-xs text-[#6B7280] mt-1">
                            <span className="flex items-center gap-1">
                              <FaIdCard size={10} /> {u.end_user_id.slice(0, 8)}...
                            </span>
                            <span className="flex items-center gap-1">
                              <FaEnvelope size={10} /> {u.email}
                            </span>
                            <span className="flex items-center gap-1">
                              <FaPhone size={10} /> {u.phone}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <span className="hidden sm:inline-block px-3 py-1 bg-purple-50 text-[#5B3A60] text-xs font-semibold rounded-full border border-purple-100">
                          {u.sessions?.length || 0} Sessions
                        </span>
                        {/* Edit button */}
                        <button
                          title="Edit identity"
                          onClick={(e) => openEdit(e, u)}
                          className="p-2 rounded-lg text-[#6B7280] hover:bg-[#B89D4F]/10 hover:text-[#B89D4F] transition-colors"
                        >
                          <FaPen size={13} />
                        </button>
                        {/* Merge button */}
                        <button
                          title="Merge with duplicate"
                          onClick={(e) => openMerge(e, u)}
                          className="p-2 rounded-lg text-[#6B7280] hover:bg-purple-50 hover:text-[#5B3A60] transition-colors"
                        >
                          <FaCodeBranch size={13} />
                        </button>
                        <div className={`p-2 rounded-lg transition-colors ${isExpanded ? "bg-[#B89D4F] text-white" : "bg-gray-100 text-gray-400"}`}>
                          {isExpanded ? <FaChevronUp /> : <FaChevronDown />}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* ── Inline Edit Mode ── */}
                  {isEditing && (
                    <div className="p-5" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-10 h-10 rounded-xl flex items-center justify-center text-base font-bold text-white bg-[#5B3A60]">
                          {u.name.charAt(0).toUpperCase()}
                        </div>
                        <p className="text-sm font-semibold text-[#5B3A60]">Edit identity — <span className="font-normal text-[#6B7280]">{u.end_user_id.slice(0, 8)}...</span></p>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
                        {/* Name */}
                        <div>
                          <label className="block text-xs font-semibold text-[#5B3A60] mb-1">Name</label>
                          <input
                            type="text"
                            value={editDraft.name}
                            onChange={e => setEditDraft(d => ({ ...d, name: e.target.value }))}
                            className={`w-full px-3 py-2 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-[#B89D4F] ${editErrors.name ? "border-red-400" : "border-[#D6C3A1]"}`}
                          />
                          {editErrors.name && <p className="text-xs text-red-500 mt-1">{editErrors.name}</p>}
                        </div>
                        {/* Email */}
                        <div>
                          <label className="block text-xs font-semibold text-[#5B3A60] mb-1">Email</label>
                          <input
                            type="email"
                            value={editDraft.email}
                            onChange={e => setEditDraft(d => ({ ...d, email: e.target.value }))}
                            className={`w-full px-3 py-2 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-[#B89D4F] ${editErrors.email ? "border-red-400" : "border-[#D6C3A1]"}`}
                          />
                          {editErrors.email && <p className="text-xs text-red-500 mt-1">{editErrors.email}</p>}
                        </div>
                        {/* Phone */}
                        <div>
                          <label className="block text-xs font-semibold text-[#5B3A60] mb-1">Phone <span className="font-normal text-[#A89CA5]">(E.164)</span></label>
                          <input
                            type="tel"
                            value={editDraft.phone}
                            onChange={e => setEditDraft(d => ({ ...d, phone: e.target.value }))}
                            placeholder="+1234567890"
                            className={`w-full px-3 py-2 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-[#B89D4F] ${editErrors.phone ? "border-red-400" : "border-[#D6C3A1]"}`}
                          />
                          {editErrors.phone && <p className="text-xs text-red-500 mt-1">{editErrors.phone}</p>}
                        </div>
                      </div>

                      {editErrors._save && (
                        <p className="text-xs text-red-500 mb-2">{editErrors._save}</p>
                      )}

                      <div className="flex items-center gap-2">
                        <button
                          onClick={(e) => saveEdit(e, u)}
                          disabled={saving}
                          className="flex items-center gap-1.5 px-4 py-2 bg-[#5B3A60] text-white text-sm rounded-lg hover:bg-[#7B5E57] transition-colors disabled:opacity-60"
                        >
                          <FaCheck size={11} /> {saving ? "Saving…" : "Save"}
                        </button>
                        <button
                          onClick={cancelEdit}
                          className="flex items-center gap-1.5 px-4 py-2 bg-gray-100 text-[#6B7280] text-sm rounded-lg hover:bg-gray-200 transition-colors"
                        >
                          <FaTimes size={11} /> Cancel
                        </button>
                      </div>
                    </div>
                  )}

                  {/* ── Expanded Session History ── */}
                  <AnimatePresence>
                    {isExpanded && !isEditing && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3, ease: "easeInOut" }}
                        className="bg-[#FAF7F5] border-t border-[#D6C3A1]/30"
                      >
                        <div className="p-6 space-y-6">
                          <h4 className="text-sm font-bold text-[#5B3A60] uppercase tracking-wide flex items-center gap-2">
                            <FaCommentAlt className="text-[#B89D4F]" />
                            Conversation History
                          </h4>

                          {(!u.sessions || u.sessions.length === 0) ? (
                            <p className="text-sm text-gray-400 italic">No conversation history recorded.</p>
                          ) : (
                            <div className="space-y-4">
                              {u.sessions.map((s) => {
                                const hasFallback = s.messages?.some(m =>
                                  m.role === "bot" && m.text?.toLowerCase().includes("couldn't find")
                                );
                                const isSessionExpanded = expandedSession === s.session_id;
                                return (
                                  <div
                                    key={s.session_id}
                                    className={`rounded-xl border overflow-hidden shadow-sm ${
                                      hasFallback ? "bg-red-50 border-red-300" : "bg-white border-[#D6C3A1]/40"
                                    }`}
                                  >
                                    <div
                                      className="px-4 py-3 bg-white flex items-center justify-between cursor-pointer hover:bg-purple-50 transition-colors"
                                      onClick={() => setExpandedSession(isSessionExpanded ? null : s.session_id)}
                                    >
                                      <div className="flex items-center gap-3">
                                        <div className="p-2 bg-purple-100 text-[#5B3A60] rounded-lg">
                                          <FaRobot size={14} />
                                        </div>
                                        <div>
                                          <p className="text-sm font-bold text-[#5B3A60]">
                                            Session {s.session_id.slice(0, 6)}...
                                          </p>
                                          <p className="text-xs text-[#A89CA5] flex items-center gap-2">
                                            <FaClock size={10} />
                                            {formatDate(s.started_at)} • {s.turn_count} turns
                                          </p>
                                        </div>
                                      </div>
                                      <div className={`text-xs font-semibold ${isSessionExpanded ? "text-[#B89D4F]" : "text-gray-400"}`}>
                                        {isSessionExpanded ? "Hide Chat" : "View Chat"}
                                      </div>
                                    </div>

                                    <AnimatePresence>
                                      {isSessionExpanded && (
                                        <motion.div
                                          initial={{ height: 0, opacity: 0 }}
                                          animate={{ height: "auto", opacity: 1 }}
                                          exit={{ height: 0, opacity: 0 }}
                                          className="border-t border-[#D6C3A1]/20 bg-[#FAF7F5]/50"
                                        >
                                          <div className="p-4 space-y-3 max-h-[400px] overflow-y-auto">
                                            {s.messages && s.messages.length > 0 ? (
                                              s.messages.map((m, i) => {
                                                const isUser = m.role === "user";
                                                return (
                                                  <div key={i} className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
                                                    <div className={`max-w-[80%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed shadow-sm ${
                                                      isUser
                                                        ? "bg-[#5B3A60] text-white rounded-br-none"
                                                        : "bg-white border border-[#D6C3A1]/50 text-[#5B3A60] rounded-bl-none"
                                                    }`}>
                                                      <span className="block text-[10px] opacity-70 mb-1 font-bold uppercase tracking-wider">
                                                        {isUser ? "You" : "Assistant"}
                                                      </span>
                                                      {m.text}
                                                      <div className="text-[10px] opacity-60 mt-1">{formatDate(m.created_at)}</div>
                                                    </div>
                                                  </div>
                                                );
                                              })
                                            ) : (
                                              <p className="text-center text-xs text-gray-400 py-4">No messages in this session.</p>
                                            )}
                                          </div>
                                        </motion.div>
                                      )}
                                    </AnimatePresence>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Merge Modal ── */}
      <AnimatePresence>
        {mergingUser && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
            onClick={closeMerge}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="flex items-center justify-between p-5 border-b border-[#D6C3A1]/30">
                <div>
                  <h2 className="text-base font-bold text-[#5B3A60] flex items-center gap-2">
                    <FaCodeBranch className="text-[#B89D4F]" />
                    Merge Records — Step {mergeStep} of 2
                  </h2>
                  <p className="text-xs text-[#6B7280] mt-0.5">
                    Source: <span className="font-semibold">{mergingUser.name}</span>
                  </p>
                </div>
                <button onClick={closeMerge} className="p-2 rounded-lg hover:bg-gray-100 text-gray-400">
                  <FaTimes />
                </button>
              </div>

              {/* Step 1 — Select Target */}
              {mergeStep === 1 && (
                <div className="p-5 space-y-4">
                  <p className="text-sm text-[#6B7280]">Select the <strong>canonical (target)</strong> record to keep. The source record will be deleted and all its sessions will be reassigned to the target.</p>

                  {/* Auto-suggestions */}
                  {duplicates.length > 0 && (
                    <div>
                      <p className="text-xs font-semibold text-[#5B3A60] uppercase tracking-wide mb-2">Suggested matches</p>
                      <div className="space-y-2">
                        {duplicates.map(d => (
                          <CandidateCard
                            key={d.end_user_id}
                            candidate={d}
                            selected={mergeTarget?.end_user_id === d.end_user_id}
                            onSelect={() => setMergeTarget(d)}
                          />
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Manual search */}
                  <div>
                    <p className="text-xs font-semibold text-[#5B3A60] uppercase tracking-wide mb-2">Search by phone or email</p>
                    <input
                      type="text"
                      placeholder="Enter phone or email…"
                      value={mergeSearch}
                      onChange={e => handleMergeSearch(e.target.value)}
                      className="w-full px-3 py-2 rounded-lg border border-[#D6C3A1] text-sm focus:outline-none focus:ring-2 focus:ring-[#B89D4F]"
                    />
                    {mergeSearching && <p className="text-xs text-[#A89CA5] mt-1">Searching…</p>}
                    {mergeSearchResults.length > 0 && (
                      <div className="mt-2 space-y-2">
                        {mergeSearchResults
                          .filter(r => !duplicates.find(d => d.end_user_id === r.end_user_id))
                          .map(d => (
                            <CandidateCard
                              key={d.end_user_id}
                              candidate={d}
                              selected={mergeTarget?.end_user_id === d.end_user_id}
                              onSelect={() => setMergeTarget(d)}
                            />
                          ))}
                      </div>
                    )}
                    {!mergeSearching && mergeSearch && mergeSearchResults.filter(r => !duplicates.find(d => d.end_user_id === r.end_user_id)).length === 0 && (
                      <p className="text-xs text-[#A89CA5] mt-1">No additional matches found.</p>
                    )}
                  </div>

                  {duplicates.length === 0 && !mergeSearch && (
                    <p className="text-sm text-[#A89CA5] italic text-center py-2">No auto-suggested duplicates found. Use the search above.</p>
                  )}

                  <div className="flex justify-end gap-2 pt-2">
                    <button onClick={closeMerge} className="px-4 py-2 rounded-lg bg-gray-100 text-[#6B7280] text-sm hover:bg-gray-200">Cancel</button>
                    <button
                      disabled={!mergeTarget}
                      onClick={() => setMergeStep(2)}
                      className="px-4 py-2 rounded-lg bg-[#5B3A60] text-white text-sm hover:bg-[#7B5E57] disabled:opacity-40 transition-colors"
                    >
                      Next: Preview →
                    </button>
                  </div>
                </div>
              )}

              {/* Step 2 — Confirm */}
              {mergeStep === 2 && mergeTarget && mergePreview && (
                <div className="p-5 space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <IdentityPreviewCard label="Source (will be deleted)" user={mergingUser} variant="source" />
                    <IdentityPreviewCard label="Target (will be kept)" user={mergeTarget} variant="target" />
                  </div>

                  <div className="bg-[#FAF7F5] rounded-xl border border-[#D6C3A1]/40 p-4">
                    <p className="text-xs font-semibold text-[#5B3A60] uppercase tracking-wide mb-2">Result after merge</p>
                    <div className="space-y-1 text-sm text-[#5B3A60]">
                      <p><span className="text-[#6B7280] w-14 inline-block">Name</span> {mergePreview.name || "—"}</p>
                      <p><span className="text-[#6B7280] w-14 inline-block">Email</span> {mergePreview.email || "—"}</p>
                      <p><span className="text-[#6B7280] w-14 inline-block">Phone</span> {mergePreview.phone || "—"}</p>
                      <p><span className="text-[#6B7280] w-14 inline-block">Sessions</span> {(mergingUser.sessions?.length || 0) + (mergeTarget.session_count || 0)} (combined)</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-xl p-3">
                    <FaExclamationTriangle className="text-amber-500 mt-0.5 flex-shrink-0" size={13} />
                    <p className="text-xs text-amber-700">This cannot be undone. All sessions, messages, and feedback from the source will be permanently reassigned to the target, and the source record will be deleted.</p>
                  </div>

                  <div className="flex items-center justify-between pt-1">
                    <button onClick={() => setMergeStep(1)} className="px-4 py-2 rounded-lg bg-gray-100 text-[#6B7280] text-sm hover:bg-gray-200">← Back</button>
                    <button
                      onClick={confirmMerge}
                      disabled={mergeLoading}
                      className="px-5 py-2 rounded-lg bg-red-600 text-white text-sm font-semibold hover:bg-red-700 disabled:opacity-60 transition-colors"
                    >
                      {mergeLoading ? "Merging…" : "Confirm Merge"}
                    </button>
                  </div>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ── Sub-components ────────────────────────────────────────────────────────────

const CandidateCard = ({ candidate, selected, onSelect }) => (
  <button
    onClick={onSelect}
    className={`w-full text-left px-4 py-3 rounded-xl border transition-all ${
      selected
        ? "border-[#5B3A60] bg-purple-50 ring-1 ring-[#5B3A60]"
        : "border-[#D6C3A1]/60 hover:border-[#B89D4F]/60 hover:bg-[#FAF7F5]"
    }`}
  >
    <p className="text-sm font-semibold text-[#5B3A60]">{candidate.name || "—"}</p>
    <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5 text-xs text-[#6B7280]">
      {candidate.email && <span><FaEnvelope size={9} className="inline mr-1" />{candidate.email}</span>}
      {(candidate.phone || candidate.whatsapp_phone) && (
        <span><FaPhone size={9} className="inline mr-1" />{candidate.phone || candidate.whatsapp_phone}</span>
      )}
      <span>{candidate.session_count} sessions</span>
    </div>
  </button>
);

const IdentityPreviewCard = ({ label, user, variant }) => (
  <div className={`rounded-xl border p-3 ${variant === "source" ? "border-red-200 bg-red-50" : "border-green-200 bg-green-50"}`}>
    <p className={`text-[10px] font-bold uppercase tracking-wide mb-1.5 ${variant === "source" ? "text-red-500" : "text-green-600"}`}>{label}</p>
    <p className="text-sm font-semibold text-[#5B3A60] truncate">{user.name || "—"}</p>
    <p className="text-xs text-[#6B7280] truncate">{user.email || "—"}</p>
    <p className="text-xs text-[#6B7280] truncate">{user.phone || "—"}</p>
    <p className="text-xs text-[#A89CA5] mt-1">{user.sessions?.length ?? user.session_count ?? 0} sessions</p>
  </div>
);

export default UserRecords;
